import React from 'react';

// In-phone app scenes for the field showcase — extracted from the retired
// parallax MobileShowcaseSection so the phone (frame, scenes, tap animation)
// survives without the scroll-jacked wrapper. Styles live in the shared
// .pl-mobile__* / .pl-mobile-showcase__* rules in ../styles.scss.

export const UserIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="8" cy="6" r="3" stroke="currentColor" strokeWidth="1.4" />
    <path d="M2.5 14C2.5 11 5 9 8 9C11 9 13.5 11 13.5 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

export const scenes = [
  {
    key: 'rfi',
    pill: 'RFI thread',
    appTitle: 'Kinetic Construction',
    screenTitle: 'View RFI',
    statusLabel: '#5 — Excess Ground water During Excavation',
    statusBadge: 'Open',
    statusTone: 'open',
    body: {
      questionAuthor: 'Courtney Henry',
      authorRole: 'Safety Instructor • Daniels Corporation…',
      question: "The InSinkErator in the original specs doesn't fit under the sink.",
      proposalsTitle: 'Proposed solutions:',
      proposals: [
        '1. Raise the countertop to allow proper clearance for the InSinkerator.',
        '2. Buy a smaller InSinkerator with the same amount of power as the original.',
      ],
      response: {
        name: 'Leslie Alexander',
        role: 'Site Engineer • Sam Superintendent',
      },
    },
    fingerTargets: [
      { top: '30%', left: '52%' },
      { top: '52%', left: '54%' },
      { top: '90%', left: '34%' },
    ],
  },
  {
    key: 'drawing',
    pill: 'Drawing markup',
    appTitle: 'Kinetic Construction',
    screenTitle: 'A-12.19 Roof Slab Edge Plan',
    statusLabel: 'R3 Current',
    statusBadge: 'Current',
    statusTone: 'current',
    body: {
      drawingMeta: [
        { label: 'Drawing Date', value: '05/20/2026' },
        { label: 'Received Date', value: '09/28/2026' },
      ],
      author: 'Tamy Magalhaes',
      annotations: [
        { color: '#fbbf24', label: 'Highlighter • Roof drain' },
        { color: '#ef4444', label: 'Markup • Edge condition' },
        { color: '#22c55e', label: 'Note • Confirm with structural' },
      ],
    },
    fingerTargets: [
      { top: '32%', left: '50%' },
      { top: '36%', left: '28%' },
      { top: '40%', left: '74%' },
      { top: '80%', left: '46%' },
    ],
  },
  {
    key: 'punch',
    pill: 'Punch item',
    appTitle: 'Kinetic Construction',
    screenTitle: 'View Punch List',
    statusLabel: '#12 — Internal Piping Installation Fixes',
    statusBadge: 'In Progress',
    statusTone: 'progress',
    body: {
      tabs: ['Checklist', 'Deficiency (4)', 'Responses'],
      activeTab: 0,
      items: [
        { id: '1.1', q: 'Are there written instructions covering any unusual hazards or complicated features?', state: 'pass' },
        { id: '1.2', q: 'Are there written instructions covering where appropriate, emergency shutdown?', state: 'pass' },
        { id: '1.3', q: 'Has instruction, in readily comprehensible form (written or verbal) on all hazards, been passed onto all those who use the equipment?', state: 'na' },
        { id: '1.4', q: 'Have any written instructions provided by the manufacturer been passed on to…', state: 'pass' },
      ],
    },
    fingerTargets: [
      { top: '40%', left: '24%' },
      { top: '55%', left: '24%' },
      { top: '70%', left: '68%' },
      { top: '86%', left: '24%' },
    ],
  },
  {
    key: 'daily',
    pill: 'Daily log',
    appTitle: 'Kinetic Construction',
    screenTitle: 'Daily Logs',
    statusLabel: 'This week',
    statusBadge: 'Live',
    statusTone: 'current',
    body: {
      logs: [
        { day: 'Wed, March 27, 2026', state: 'today', subtitle: 'Today', meta: '4 entries • 2 photos' },
        { day: 'Tue, March 26, 2026', state: 'pending', subtitle: 'Pending', meta: '4 entries • 2 photos' },
        { day: 'Mon, March 25, 2026', state: 'completed', subtitle: 'Completed', meta: '2 crews • 3 hours • 4 photos' },
        { day: 'Sat, March 23, 2026', state: 'completed', subtitle: 'Completed', meta: '3 crews • 2 hours • 6 photos' },
      ],
    },
    fingerTargets: [
      { top: '34%', left: '50%' },
      { top: '48%', left: '50%' },
      { top: '62%', left: '50%' },
      { top: '78%', left: '50%' },
    ],
  },
  {
    key: 'schedule',
    pill: 'Schedule update',
    appTitle: 'Kinetic Construction',
    screenTitle: 'Project Schedule',
    statusLabel: 'Phase 3 — Interior Fitout',
    statusBadge: 'On Track',
    statusTone: 'current',
    body: {
      milestones: [
        { name: 'Concrete pour — Level 4', date: 'Mar 28', status: 'done', percent: 100 },
        { name: 'MEP rough-in — Level 3', date: 'Apr 02', status: 'progress', percent: 62 },
        { name: 'Drywall — Level 2', date: 'Apr 09', status: 'progress', percent: 28 },
        { name: 'Final inspection — Lobby', date: 'Apr 18', status: 'upcoming', percent: 0 },
      ],
    },
    fingerTargets: [
      { top: '34%', left: '50%' },
      { top: '50%', left: '50%' },
      { top: '66%', left: '50%' },
      { top: '82%', left: '50%' },
    ],
  },
];

export function PunchListBody({ body, revealedCount = body.items.length, currentStep = -1, isTapping = false }) {
  return (
    <>
      <div className="pl-mobile__tabs">
        {body.tabs.map((tab, idx) => (
          <span key={tab} className={`pl-mobile__tab${idx === body.activeTab ? ' pl-mobile__tab--active' : ''}`}>
            {tab}
          </span>
        ))}
      </div>
      <div className="pl-mobile__punch-list">
        {body.items.map((item, idx) => {
          const isRevealed = idx < revealedCount;
          const isFocused = idx === currentStep;
          return (
            <div
              className={`pl-mobile__punch-item${isFocused ? ' is-focused' : ''}${isFocused && isTapping ? ' is-pressing' : ''}`}
              key={item.id}
            >
              <p>
                <span className="pl-mobile__punch-id">#{item.id}</span> {item.q}
              </p>
              <div className="pl-mobile__punch-actions">
                <span className={`pl-mobile__punch-btn${isRevealed && item.state === 'pass' ? ' is-active is-pass' : ''}`}>Pass</span>
                <span className={`pl-mobile__punch-btn${isRevealed && item.state === 'fail' ? ' is-active is-fail' : ''}`}>Fail</span>
                <span className={`pl-mobile__punch-btn${isRevealed && item.state === 'na' ? ' is-active is-na' : ''}`}>N/A</span>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

export function RfiBody({ body }) {
  return (
    <>
      <div className="pl-mobile__rfi-question">
        <div className="pl-mobile__rfi-question-head">
          <div className="pl-mobile__avatar">
            <UserIcon />
          </div>
          <div>
            <strong>{body.questionAuthor}</strong>
            <span>{body.authorRole}</span>
          </div>
        </div>
        <p className="pl-mobile__rfi-text">{body.question}</p>
        <p className="pl-mobile__rfi-proposals-title">{body.proposalsTitle}</p>
        {body.proposals.map((proposal, idx) => (
          <p key={idx} className="pl-mobile__rfi-proposal">
            {proposal}
          </p>
        ))}
      </div>
      <div className="pl-mobile__rfi-response">
        <p className="pl-mobile__rfi-response-label">Responses</p>
        <div className="pl-mobile__rfi-question-head">
          <div className="pl-mobile__avatar pl-mobile__avatar--alt">
            <UserIcon />
          </div>
          <div>
            <strong>{body.response.name}</strong>
            <span>{body.response.role}</span>
          </div>
        </div>
        <div className="pl-mobile__rfi-response-action">Set Official Response</div>
      </div>
    </>
  );
}

export function DrawingBody({ body }) {
  return (
    <>
      <div className="pl-mobile__drawing-canvas" aria-hidden="true">
        <div className="pl-mobile__drawing-grid" />
        <div className="pl-mobile__drawing-shape pl-mobile__drawing-shape--a" />
        <div className="pl-mobile__drawing-shape pl-mobile__drawing-shape--b" />
        <div className="pl-mobile__drawing-shape pl-mobile__drawing-shape--c" />
        <div className="pl-mobile__drawing-marker pl-mobile__drawing-marker--yellow" />
        <div className="pl-mobile__drawing-marker pl-mobile__drawing-marker--red" />
      </div>
      <div className="pl-mobile__drawing-meta">
        {body.drawingMeta.map((item) => (
          <div key={item.label}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </div>
      <div className="pl-mobile__drawing-author">
        <div className="pl-mobile__avatar">
          <UserIcon />
        </div>
        <span>{body.author}</span>
      </div>
      <div className="pl-mobile__drawing-annotations">
        {body.annotations.map((annotation) => (
          <div key={annotation.label} className="pl-mobile__annotation">
            <span className="pl-mobile__annotation-dot" style={{ background: annotation.color }} />
            <span>{annotation.label}</span>
          </div>
        ))}
      </div>
    </>
  );
}

export function DailyBody({ body, currentStep = -1, isTapping = false }) {
  return (
    <div className="pl-mobile__daily-list">
      {body.logs.map((log, idx) => {
        const isFocused = idx === currentStep;
        return (
          <div
            key={log.day}
            className={`pl-mobile__daily-row pl-mobile__daily-row--${log.state}${isFocused ? ' is-focused' : ''}${isFocused && isTapping ? ' is-pressing' : ''}`}
          >
            <div className="pl-mobile__daily-row-top">
              <strong>{log.day}</strong>
              <span className={`pl-mobile__daily-tag pl-mobile__daily-tag--${log.state}`}>{log.subtitle}</span>
            </div>
            <span className="pl-mobile__daily-meta">{log.meta}</span>
          </div>
        );
      })}
    </div>
  );
}

export function ScheduleBody({ body, currentStep = -1, isTapping = false }) {
  return (
    <div className="pl-mobile__schedule-list">
      {body.milestones.map((m, idx) => {
        const isFocused = idx === currentStep;
        return (
          <div
            key={m.name}
            className={`pl-mobile__milestone pl-mobile__milestone--${m.status}${isFocused ? ' is-focused' : ''}${isFocused && isTapping ? ' is-pressing' : ''}`}
          >
            <div className="pl-mobile__milestone-top">
              <strong>{m.name}</strong>
              <span>{m.date}</span>
            </div>
            <div className="pl-mobile__milestone-bar">
              <div className="pl-mobile__milestone-fill" style={{ width: `${m.percent}%` }} />
            </div>
            <span className="pl-mobile__milestone-status">
              {m.status === 'done' && 'Complete'}
              {m.status === 'progress' && `${m.percent}% complete`}
              {m.status === 'upcoming' && 'Upcoming'}
            </span>
          </div>
        );
      })}
    </div>
  );
}
